/**
 * Service-worker registration (SPEC §10).
 *
 * `registerType: 'prompt'` — a waiting worker NEVER activates on its own. The
 * banner asks, and only 更新 hands control over, because auto-activating
 * mid-turn would reload the page and drop the open `/api/stream` socket at the
 * exact moment the user is watching something happen.
 *
 * Registration happens once per document, but the banner lives inside a shell
 * that remounts on navigation — so the "update ready" signal is kept in module
 * state and replayed to whoever subscribes next. Without that replay, an update
 * detected on the chat screen would be invisible everywhere else.
 *
 * The worker itself is still generated by `vite-plugin-pwa` (`vite.config.ts`,
 * `injectRegister: null`); only the ~30 lines that register it are ours. The
 * plugin's `virtual:pwa-register` module would do the same job, but it loads
 * `workbox-window` from `apps/web`, which under pnpm's strict layout forces a
 * direct dependency on a package this app otherwise has no use for — and with
 * it a `pnpm-lock.yaml` edit outside this module's ownership, without which a
 * clean checkout cannot `pnpm install --frozen-lockfile`. The prompt flow it
 * provides is three DOM events, so the app owns them instead and the lockfile
 * stays untouched.
 */

/** Emitted by `generateSW` at the `dist` root; served `no-store` (CONTRACTS §4.8). */
const SW_URL = '/sw.js';
const SW_SCOPE = '/';

/**
 * The message the generated worker listens for. `skipWaiting: false` means it
 * hands over on this message and on nothing else, which is the whole of
 * "never auto-reload mid-turn".
 */
const SKIP_WAITING = { type: 'SKIP_WAITING' } as const;

let registered = false;
let needRefresh = false;
let waitingWorker: ServiceWorker | null = null;
const listeners = new Set<() => void>();

function announce(worker: ServiceWorker): void {
  waitingWorker = worker;
  needRefresh = true;
  for (const listener of listeners) listener();
}

/**
 * Wires the 「有新版本」 prompt onto one `ServiceWorkerContainer`.
 *
 * Exported so the flow can be driven against a fake container in tests — a real
 * one needs a served worker and a second page load. The app reaches it through
 * `subscribeServiceWorkerUpdate`.
 */
export async function watchServiceWorkerUpdates(
  container: ServiceWorkerContainer,
  reload: () => void,
): Promise<void> {
  let reloaded = false;
  container.addEventListener('controllerchange', () => {
    // Reached only after 更新: the worker never calls `skipWaiting()` on its
    // own, so a controller change means the user asked for this one.
    if (reloaded || waitingWorker === null) return;
    reloaded = true;
    reload();
  });

  let registration: ServiceWorkerRegistration;
  try {
    registration = await container.register(SW_URL, { scope: SW_SCOPE });
  } catch {
    // A worker that will not register costs the user offline shell caching;
    // taking the app down over it would cost them the app.
    return;
  }

  const offerIfWaiting = (worker: ServiceWorker | null): void => {
    // A worker reaching `installed` while another one already controls the page
    // is an UPDATE waiting for permission. The very first install controls
    // nothing and has no predecessor to replace — prompting there would offer
    // the user the version they are already looking at.
    if (worker === null || worker.state !== 'installed' || container.controller === null) return;
    announce(worker);
  };

  offerIfWaiting(registration.waiting);
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (installing === null) return;
    installing.addEventListener('statechange', () => offerIfWaiting(installing));
  });
}

/** Subscribe to "a new version is waiting". Returns an unsubscribe function. */
export function subscribeServiceWorkerUpdate(onNeedRefresh: () => void): () => void {
  listeners.add(onNeedRefresh);

  if (!registered) {
    registered = true;
    // `vite dev` emits no worker, so registering there would 404 on every load
    // (the plugin's own register module is likewise inert in dev).
    if (import.meta.env.PROD && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      void watchServiceWorkerUpdates(navigator.serviceWorker, () => window.location.reload());
    }
  }
  if (needRefresh) onNeedRefresh();

  return () => listeners.delete(onNeedRefresh);
}

export function applyServiceWorkerUpdate(): void {
  waitingWorker?.postMessage(SKIP_WAITING);
}

/** `beforeinstallprompt` capture for the 安裝 App section of 設定. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: InstallPromptEvent | null = null;

export function watchInstallPrompt(onAvailable: (available: boolean) => void): () => void {
  const handler = (event: Event): void => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    onAvailable(true);
  };
  const installed = (): void => {
    deferredPrompt = null;
    onAvailable(false);
  };
  window.addEventListener('beforeinstallprompt', handler);
  window.addEventListener('appinstalled', installed);
  if (deferredPrompt !== null) onAvailable(true);
  return () => {
    window.removeEventListener('beforeinstallprompt', handler);
    window.removeEventListener('appinstalled', installed);
  };
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice.outcome === 'accepted';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
